import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Loader2, Receipt, X } from 'lucide-react';
import { getChangeOrderReference } from '../../utils/changeOrders';
import type { ChangeOrder, SalesOrder } from '../../types';
import './ProgressInvoiceConsolidateModal.css';

const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

export interface ConsolidateCoRow {
  changeOrder: ChangeOrder;
  salesOrder?: SalesOrder & { id: string };
  amount: number;
}

export interface ProgressInvoiceConsolidateModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedChangeOrderIds: string[]) => Promise<void>;
  installmentLabel: string;
  installmentDetail: string;
  installmentAmount: number;
  coAdjustments?: Array<{ reference: string; amount: number; title: string }>;
  changeOrders: ConsolidateCoRow[];
  submitting: boolean;
  error?: string | null;
}

const ProgressInvoiceConsolidateModal: React.FC<ProgressInvoiceConsolidateModalProps> = ({
  open,
  onClose,
  onConfirm,
  installmentLabel,
  installmentDetail,
  installmentAmount,
  coAdjustments = [],
  changeOrders,
  submitting,
  error,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(changeOrders.map(r => r.changeOrder.id!).filter(Boolean)));
    }
  }, [open, changeOrders]);

  const selectedRows = useMemo(
    () => changeOrders.filter(r => r.changeOrder.id && selectedIds.has(r.changeOrder.id)),
    [changeOrders, selectedIds],
  );

  const coTotal = useMemo(
    () => selectedRows.reduce((s, r) => s + r.amount, 0),
    [selectedRows],
  );

  const invoiceTotal = installmentAmount + coTotal;
  const hasExistingInvoices = selectedRows.some(r => !!r.salesOrder?.quickbooksInvoiceId);

  useEffect(() => {
    if (!open || submitting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const toggleCo = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    void onConfirm(Array.from(selectedIds));
  };

  return createPortal(
    <div className="picm-overlay" onClick={submitting ? undefined : onClose}>
      <div className="picm-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="picm-head">
          <div>
            <h3>Review progress invoice</h3>
            <p>Confirm the 40% installment and any change orders before creating in QuickBooks.</p>
          </div>
          <button type="button" className="picm-close" onClick={onClose} disabled={submitting} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="picm-body">
          <section className="picm-section">
            <h4>Progress installment</h4>
            <div className="picm-line picm-line--main">
              <div>
                <span className="picm-line-label">{installmentLabel}</span>
                <span className="picm-line-detail">{installmentDetail}</span>
              </div>
              <span className="picm-line-amount">{fmt(installmentAmount)}</span>
            </div>
            {coAdjustments.map((adj, i) => (
              <div key={`${adj.reference}-${i}`} className="picm-line picm-line--adj">
                <span className="picm-line-detail">
                  {adj.reference} {adj.title}
                </span>
                <span className={`picm-line-amount ${adj.amount < 0 ? 'picm-line-amount--credit' : ''}`}>
                  {adj.amount >= 0 ? '+' : '−'}{fmt(Math.abs(adj.amount))}
                </span>
              </div>
            ))}
          </section>

          {changeOrders.length > 0 && (
            <section className="picm-section">
              <h4>Change orders to include</h4>
              <p className="picm-section-note">
                {hasExistingInvoices
                  ? 'Open QuickBooks invoices for selected change orders will be voided (not deleted) and reissued as line items on this progress invoice.'
                  : 'Selected change orders will be added as line items on this invoice.'}
              </p>
              {hasExistingInvoices && (
                <p className="picm-section-note picm-section-note--warn">
                  If any CO invoice falls in a closed QuickBooks accounting period, void it manually in
                  QuickBooks first, then run this again. CO invoices already deleted in QuickBooks are fine —
                  they will be skipped and added to this invoice.
                </p>
              )}
              <ul className="picm-co-list">
                {changeOrders.map(row => {
                  const id = row.changeOrder.id!;
                  const ref = getChangeOrderReference(row.changeOrder);
                  const checked = selectedIds.has(id);
                  const existingNum = row.salesOrder?.quickbooksInvoiceNum;
                  return (
                    <li key={id} className="picm-co-item">
                      <label className="picm-co-label">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={submitting}
                          onChange={() => toggleCo(id)}
                        />
                        <span className="picm-co-info">
                          <span className="picm-co-ref">{ref}</span>
                          <span className="picm-co-title">{row.changeOrder.title || 'Change order'}</span>
                          {existingNum && (
                            <span className="picm-co-qb">Will void QB #{existingNum}</span>
                          )}
                        </span>
                        <span className="picm-co-amount">{fmt(row.amount)}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <div className="picm-total">
            <span>New invoice total</span>
            <strong>{fmt(invoiceTotal)}</strong>
          </div>

          {error && (
            <div className="picm-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}
        </div>

        <footer className="picm-foot">
          <button type="button" className="picm-btn picm-btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="picm-btn picm-btn--primary"
            disabled={submitting}
            onClick={handleConfirm}
          >
            {submitting ? <Loader2 size={14} className="picm-spin" /> : <Receipt size={14} />}
            {submitting ? 'Creating invoice…' : 'Create consolidated invoice'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default ProgressInvoiceConsolidateModal;
